import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { roles } from '@nxsys/store/constants';
import { IRELAND } from '@nxsys/store/constants/keys';
import { companyActions } from '@nxsys/store/slices/company/reducer/companySlice';
import { GetEmailTemplateCompanyListParams } from '@nxsys/store/slices/company/types';
import { Icons } from '@nxsys/types/icons';
import { ICompany } from '@nxsys/types/models/company';
import { IEmailTemplate } from '@nxsys/types/models/emailTemplate';
import { formatStatus } from '@nxsys/utils';
import { ColumnsType } from 'antd/lib/table';
import { debounce } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Bureau mode mirrors bureau-portal/src/pages/Company/CompanyUpdate/components/EmailTemplates/index.jsx
// (lines 34-99): whitelist by user.role + user.payrollCountry. Umbrella mode is unfiltered to
// preserve cis-frontend's no-filter behavior (cis-frontend/src/api/templateEmail.js:6).
const getBureauTemplateIds = (
  role: string | undefined,
  isIreland: boolean,
): string[] => {
  if (role === roles.employer) {
    return isIreland
      ? [
          'subcontractor_login',
          'subcontractor_registration_complete',
          'self_bill',
          'registration_complete_from_onboarding',
          'onboarding_progress_request',
        ]
      : [
          'subcontractor_login',
          'subcontractor_registration_complete',
          'self_bill',
          'EPS_submission',
          'KID_template',
          'registration_complete_from_onboarding',
          'onboarding_progress_request',
        ];
  }

  return isIreland
    ? [
        'subcontractor_login',
        'subcontractor_registration_complete',
        'invoice',
        'self_bill',
        'credit_note',
        'registration_complete_from_onboarding',
        'onboarding_progress_request',
      ]
    : [
        'subcontractor_login',
        'subcontractor_registration_complete',
        'invoice',
        'self_bill',
        'credit_note',
        'EPS_submission',
        'KID_template',
        'registration_complete_from_onboarding',
        'onboarding_progress_request',
      ];
};

// Mirrors bureau-portal/src/store/slices/company/reducer/companySlice.js:46-58:
// renames Subcontractor → Employee and Self Bill → Payslips for display.
// (CIS300_submission filter from that reducer is dead code — never in the templateIds
// whitelist, so the backend never returns it.)
type DisplayEmailTemplate = IEmailTemplate & {
  subjectNew?: string;
  templateIdNew?: string;
};

const applyBureauDisplayTransform = (
  list: IEmailTemplate[],
): DisplayEmailTemplate[] =>
  list.map((row): DisplayEmailTemplate => {
    if (
      row?.templateId === 'subcontractor_login' ||
      row?.templateId === 'subcontractor_registration_complete'
    ) {
      return {
        ...row,
        subjectNew: (row.subject || '').replace(/Subcontractor/gi, 'Employee'),
        templateIdNew: (row.templateId || '').replace(
          /subcontractor/gi,
          'employee',
        ),
      };
    }
    if (row?.templateId === 'self_bill') {
      return {
        ...row,
        subjectNew: (row.subject || '').replace(/Self Bill/gi, 'Payslips'),
        templateIdNew: (row.templateId || '').replace(/self_bill/gi, 'payslips'),
      };
    }
    return row;
  });

type Props = {
  data?: ICompany;
};

const EmailTemplates: React.FC<Props> = ({ data }) => {
  const { messages } = useTypedIntl();
  const navigate = useNavigateWithQuery();
  const dispatch = useDispatch();
  const { emailTemplateCompanyList } = useSelector(
    (state: AppState) => state.companyStore,
  );
  const { isBureau, user, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const { list, loading, pagination, queryParams } = emailTemplateCompanyList;

  const templateIds = useMemo(() => {
    if (!isBureau) return undefined;
    return getBureauTemplateIds(user?.role, payrollCountry === IRELAND);
  }, [isBureau, user?.role, payrollCountry]);

  const displayList = useMemo<DisplayEmailTemplate[]>(() => {
    const safeList = list ?? [];
    return isBureau ? applyBureauDisplayTransform(safeList) : safeList;
  }, [isBureau, list]);

  useEffect(() => {
    if (data?.id) {
      handleFetchData({
        page: 0,
        size: 20,
        templateIds,
      });
    }
  }, [data?.id, templateIds]);

  const handleFetchData = (params: GetEmailTemplateCompanyListParams) => {
    const newParams = { ...params, companyId: data?.id || '' };
    dispatch(companyActions.getEmailTemplateCompanyList(newParams));
  };

  const debouncedFetchData = useCallback(
    debounce((search: string) => {
      handleFetchData({
        ...queryParams,
        page: 0,
        size: 20,
        search: search,
      });
    }, 500),
    [queryParams],
  );

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleFetchData({
        ...queryParams,
        sortField: sorter.field ?? '',
        sortType: sorter.order,
      });
    } else {
      handleFetchData({
        ...queryParams,
        sortField: undefined,
        sortType: undefined,
      });
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    debouncedFetchData(value);
  };

  const columns: ColumnsType<DisplayEmailTemplate> = [
    {
      key: 'action',
      title: messages['common.action'],
      width: 100,
      align: 'left',
      render: (record) => (
        <ul className="flex items-center justify-start gap-x-2">
          <li onClick={() => handleEditEmailTemplate(record.id)}>
            <Icons.IconEdit />
          </li>
        </ul>
      ),
    },
    {
      key: 'status',
      title: messages['common.status'],
      dataIndex: 'status',
      width: 150,
      render: (status) => (
        <AppTagStatus className={`tag-popup ${formatStatus(status)}`}>
          {formatStatus(status)}
        </AppTagStatus>
      ),
    },
    {
      key: 'templateId',
      title: messages['company.emailTemplate.templateId'],
      sorter: true,
      width: 400,
      sortDirections: ['descend', 'ascend'],
      render: (_, record) => record.templateIdNew || record.templateId,
    },
    {
      key: 'subject',
      title: messages['company.emailTemplate.subject'],
      width: 300,
      render: (_, record) => record.subjectNew || record.subject,
    },
    {
      key: 'updatedAt',
      title: messages['common.updatedOn'],
      dataIndex: 'updatedAt',
      width: 200,
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
    },
    {
      key: 'updatedBy',
      title: messages['common.updatedBy'],
      dataIndex: 'updatedBy',
      width: 200,
      render: (value) =>
        [value?.forename, value?.surname].filter(Boolean).join(' ') || '-',
    },
  ];

  const handleEditEmailTemplate = (id: string) => {
    navigate(`/companies/${data?.id}/email-templates/${id}/edit`, {
      query: {
        from: `/companies/${data?.id}`,
      },
    });
  };

  return (
    <AppsContent>
      <AppsHeader>
        <div className="flex items-center gap-x-2 w-full justify-between">
          <AppInputSearch onChange={handleSearchChange} />
        </div>
      </AppsHeader>
      <AppTableContainer
        data={displayList}
        loading={loading}
        page={pagination.current}
        total={pagination.totalDocs}
        pageSize={pagination.pageSize}
        scroll={{ x: 800, y: 700 }}
        titleEmpty={messages['common.noData']}
        columns={columns}
        onChangePagination={onChangePagination}
        onChangeTable={onChangeTable}
      />
    </AppsContent>
  );
};

export default EmailTemplates;
